import React, { useEffect, useMemo, useId } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Avatar, Tabs, Tab, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBack from '@mui/icons-material/ArrowBack';
import SportsSoccer from '@mui/icons-material/SportsSoccer';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import Groups from '@mui/icons-material/Groups';
import Gavel from '@mui/icons-material/Gavel';
import QueryStats from '@mui/icons-material/QueryStats';
import PersonOutline from '@mui/icons-material/PersonOutline';
import { useTranslation } from 'react-i18next';
import { EmptyState, Loading } from '../design-system';
import MatchTimeline from '../components/MatchTimeline';
import MatchStatistics from '../components/MatchStatistics';
import MatchLineupPitch from '../components/matchRecord/MatchLineupPitch';
import MatchPlayerStatistics from '../components/matchRecord/MatchPlayerStatistics';
import FollowPlayerButton from '../components/scouting/FollowPlayerButton';
import { useActaPartido } from '../hooks/queries';
import usePageTitle from '../hooks/usePageTitle';
import { useAuth } from '../context/AuthContext';
import { UpgradeBoundary } from '../components/license/LicenseGate';
import { matchPlayerName, matchValue, startingPlayers } from '../utils/matchRecord';
import { isGoalkeeper } from '../utils/playerDiscovery';
import PlayerRoleBadge from '../components/playerStatistics/PlayerRoleBadge';
import '../components/matchRecord/MatchRecord.css';

export const getEventRowColors = (theme, tone) => {
  const color = theme.palette[tone]?.main || theme.palette.primary.main;
  const dark = theme.palette.mode === 'dark';
  return {
    backgroundColor: alpha(color, dark ? 0.12 : 0.07),
    hoverBackgroundColor: alpha(color, dark ? 0.18 : 0.11),
    borderColor: alpha(color, dark ? 0.55 : 0.24),
    accentColor: color,
  };
};

const eventOrder = event => {
  const match = String(event.minuto ?? event.minute ?? '').match(/^(\d+)(?:\+(\d+))?$/);
  return match ? Number(match[1]) + Number(match[2] || 0) / 100 : Infinity;
};

function PlayerRoster({ players, name, shield, teamId, canFollow, onPlayer }) {
  const { t } = useTranslation('matches');
  const starters = startingPlayers(players);
  const subs = (players || []).filter(player => !player.titular).sort((a, b) => (a.numero ?? Infinity) - (b.numero ?? Infinity));
  return <section className="mr-panel mr-roster" aria-label={name}>
    <header className="mr-roster-heading"><Avatar src={shield} alt="" /><h3>{name}</h3></header>
    {[[t('lineup.starters'), starters], [t('lineup.subs'), subs]].map(([label, squad]) => <div key={label}>
      <h4>{label} <span>({squad.length})</span></h4>
      {!squad.length && <p className="mr-help">{t('record.noRoster')}</p>}
      <ul>{squad.map((player, index) => <li key={player.codJugador || player.id || index}
        className={isGoalkeeper(player) ? 'player-goalkeeper' : undefined}>
        <span className={`mr-roster-number${player.portero ? ' mr-roster-keeper' : ''}`}>{matchValue(player.numero)}</span>
        <button type="button" className="mr-player-link" onClick={() => player.codJugador && onPlayer(player.codJugador)}>
          {matchPlayerName(player.nombre)}{player.capitan && <span className="mr-captain" aria-label={t('record.captain')}>C</span>}
          {player.posicion && <small>{typeof player.posicion === 'object' ? player.posicion.name || player.posicion.nombre : player.posicion}</small>}</button>
        <PlayerRoleBadge player={player} />
        {player.valoracion !== undefined && player.valoracion !== null && player.valoracion !== '' && <span className="mr-roster-rating">{player.valoracion}</span>}
        {canFollow && <FollowPlayerButton jugadorId={player.codJugador || player.id} jugadorNombre={player.nombre || ''}
          equipoNombre={name || ''} equipoId={teamId || null} size="small" />}
      </li>)}</ul>
    </div>)}
  </section>;
}

export default function ActaPartidoPageModern() {
  const { t, i18n } = useTranslation('matches');
  usePageTitle(t('record.title'));
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [eventFilter, setEventFilter] = React.useState('all');
  const tabId = useId();
  const { hasPermission } = useAuth();
  const canUseScouting = hasPermission('scouting.view');
  const state = location.state || {};
  const season = state.idTemporada ?? state.partido?.idTemporada ?? state.partido?.idtemporada ?? params.get('idTemporada');
  const code = state.codActa ?? state.partido?.codacta ?? state.partido?.codActa ?? state.partido?.idActa ?? params.get('codActa');
  const { data, isLoading, refetch } = useActaPartido(season, code, { enabled: !state.acta && !!season && !!code });
  const acta = state.acta || (Array.isArray(data) ? data[0] : data);
  const partido = useMemo(() => {
    const match = { ...acta, ...state.partido };
    return {
      ...match,
      equipoLocal: match.equipoLocal || t('record.home'),
      equipoVisitante: match.equipoVisitante || t('record.away'),
    };
  }, [acta, state.partido, t]);
  const events = useMemo(() => ['goles', 'tarjetas', 'sustituciones'].flatMap((field, index) =>
    ['Locales', 'Visitantes'].flatMap(suffix => (acta?.[`${field}${suffix}`] || []).map(event => ({
      ...event, kind: ['gol', 'tarjeta', 'sustitucion'][index], home: suffix === 'Locales',
    })))).sort((a, b) => eventOrder(a) - eventOrder(b)), [acta]);
  useEffect(() => {
    document.documentElement.lang = i18n.resolvedLanguage || i18n.language;
  }, [i18n.resolvedLanguage, i18n.language]);
  const periods = ['total', 'primeraParte', 'segundaParte'];
  const hasStatistics = periods.some(period => acta?.estadisticasPartido?.[period]?.length > 0);
  const hasPlayers = acta?.estadisticasJugadores?.jugadores?.length > 0;
  const tabs = [
    { key: 'lineups', label: t('tabs.lineups'), icon: <Groups /> },
    { key: 'events', label: `${t('tabs.events')}${events.length ? ` (${events.length})` : ''}`, icon: <SportsSoccer /> },
    ...(hasStatistics ? [{ key: 'statistics', label: t('record.statistics'), icon: <QueryStats /> }] : []),
    ...(hasPlayers ? [{ key: 'players', label: t('record.players'), icon: <PersonOutline /> }] : []),
    { key: 'info', label: t('record.info'), icon: <Gavel /> },
  ];
  const active = tabs.some(tab => tab.key === params.get('section')) ? params.get('section') : 'lineups';
  const onPlayer = id => {
    const query = new URLSearchParams({ idJugador: id });
    if (season) query.set('idTemporada', season);
    navigate(`/jugador?${query}`);
  };
  if (isLoading && !state.acta) return <div className="mr-record" data-theme={theme.palette.mode}><Loading variant="soccer" text={t('loading.acta')} /></div>;
  if (!acta) return <div className="mr-record" data-theme={theme.palette.mode}><EmptyState type="error"
    title={t('errors.notFound')} message={t('errors.loadFailed')} onAction={season && code ? () => refetch() : () => navigate(-1)}
    actionLabel={t(season && code ? 'errors.retry' : 'errors.back')} onSecondaryAction={season && code ? () => navigate(-1) : undefined}
    secondaryActionLabel={t('errors.back')} /></div>;
  const sides = [
    { key: 'home', name: partido.equipoLocal, shield: partido.escudoEquipoLocal, id: partido.codEquipoLocal ?? partido.idEquipoLocal, players: acta.jugadoresLocales, goals: acta.golesLocales },
    { key: 'away', name: partido.equipoVisitante, shield: partido.escudoEquipoVisitante, id: partido.codEquipoVisitante ?? partido.idEquipoVisitante, players: acta.jugadoresVisitantes, goals: acta.golesVisitantes },
  ];
  const teamLink = side => {
    const competition = partido.idCompeticion ?? acta.idCompeticion;
    const group = partido.idGrupo ?? acta.idGrupo;
    const teamSeason = season ?? acta.idTemporada;
    if ([side.id, competition, group, teamSeason].some(value => value == null || String(value).trim() === '')) {
      return <small>{t('record.teamStatisticsUnavailable')}</small>;
    }
    const query = new URLSearchParams({
      id: `${side.id}_${competition}_${group}_${teamSeason}`, idTemporada: String(teamSeason),
    });
    return <Link className="mr-team-statistics-link" to={`/estadisticasEquipos?${query}`}
      state={{ directoryFrom: location.pathname + location.search }}
      aria-label={t('record.teamStatisticsNamed', { team: side.name })}>
      <QueryStats aria-hidden="true" />{t('record.teamStatistics')}
    </Link>;
  };
  const localCards = acta.tarjetasLocales?.length || 0;
  const awayCards = acta.tarjetasVisitantes?.length || 0;
  return <article className="mr-record" data-theme={theme.palette.mode}>
    <header className="mr-page-heading"><div><span className="mr-eyebrow">{t('record.eyebrow')}</span>
      <h1>{t('record.title')}</h1><p>{t('record.subtitle')}</p></div>
      <button type="button" className="mr-back" onClick={() => navigate(-1)}><ArrowBack aria-hidden="true" />{t('back')}</button></header>
    <section className="mr-scoreboard" aria-label={t('record.scoreboard')}>
      <div className="mr-match-meta">{[acta.fecha || partido.fecha, acta.hora || partido.hora, acta.campo || partido.campo || partido.estadio]
        .filter(Boolean).map((value, index) => <span key={index}>{value}</span>)}</div>
      <div className="mr-scoreboard-main">
        <div className="mr-scoreboard-team"><Avatar src={sides[0].shield} alt="" /><h2>{sides[0].name}</h2><small>{t('record.home')}</small>{teamLink(sides[0])}</div>
        <div className="mr-score"><span>{t(partido.partidoJugado === true ? 'status.finished' : 'record.matchRecord')}</span>
          <strong>{matchValue(partido.golesCasa)} <i>–</i> {matchValue(partido.golesVisitante)}</strong></div>
        <div className="mr-scoreboard-team"><Avatar src={sides[1].shield} alt="" /><h2>{sides[1].name}</h2><small>{t('record.away')}</small>{teamLink(sides[1])}</div>
      </div>
      <div className="mr-scorers">{sides.map((side, sideIndex) => <div key={side.key}>{[
        ...(side.goals || []).filter(goal => String(goal.tipoGol) !== '102'),
        ...(sides[1 - sideIndex].goals || []).filter(goal => String(goal.tipoGol) === '102'),
      ].map((goal, index) =>
        <span key={index}><SportsSoccer aria-hidden="true" />{matchPlayerName(goal.nombreJugador)} <b>{goal.minuto}′</b>
          {String(goal.tipoGol) === '101' ? ` (${t('record.penalty')})` : String(goal.tipoGol) === '102' ? ` (${t('record.ownGoal')})` : ''}</span>)}</div>)}</div>
    </section>
    <Tabs className="mr-tabs" value={active} onChange={(_, value) => setParams(current => {
      const next = new URLSearchParams(current);
      next.set('section', value);
      if (season && code) {
        next.set('idTemporada', season);
        next.set('codActa', code);
      }
      return next;
    }, { state: location.state })} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile aria-label={t('record.sections')}>
      {tabs.map(tab => <Tab key={tab.key} value={tab.key} label={tab.label} icon={tab.icon} iconPosition="start"
        id={`${tabId}-${tab.key}`} aria-controls={`${tabId}-panel`} />)}
    </Tabs>
    <div className="mr-tab-content" id={`${tabId}-panel`} role="tabpanel" aria-labelledby={`${tabId}-${active}`}>
      {active === 'lineups' && <>
        <MatchLineupPitch acta={acta} partido={partido} onPlayer={onPlayer} />
        <div className="mr-rosters">{sides.map(side => <PlayerRoster key={side.key} players={side.players} name={side.name}
          shield={side.shield} teamId={side.id} canFollow={canUseScouting} onPlayer={onPlayer} />)}</div>
      </>}
      {active === 'statistics' && <MatchStatistics estadisticas={acta.estadisticasPartido}
        equipoLocal={partido.equipoLocal} equipoVisitante={partido.equipoVisitante}
        escudoLocal={partido.escudoEquipoLocal} escudoVisitante={partido.escudoEquipoVisitante} />}
      {active === 'players' && <MatchPlayerStatistics acta={acta} partido={partido} onPlayer={onPlayer} />}
      {active === 'events' && <>
        {events.some(event => Number.isFinite(eventOrder(event))) && <section className="mr-panel mr-timeline">
          <MatchTimeline eventos={events.filter(event => Number.isFinite(eventOrder(event))).map(event => ({
            ...event, tipo: event.kind, local: event.home, minuto: parseInt(event.minuto ?? event.minute, 10),
            jugador: event.nombreJugador, jugadorEntra: event.nombreJugadorEntra, jugadorSale: event.nombreJugadorSale,
            tipoTarjeta: Number(event.tipo ?? event.tipoTarjeta),
          }))} equipoLocal={partido.equipoLocal} equipoVisitante={partido.equipoVisitante}
            escudoLocal={partido.escudoEquipoLocal} escudoVisitante={partido.escudoEquipoVisitante}
            duracionTotal={state.minutosJuego || acta.minutosJuego || partido.minutosJuego || 90} />
        </section>}
        <section className="mr-panel" aria-label={t('tabs.events')}>
          <div className="mr-segmented mr-event-filters" role="group" aria-label={t('record.eventFilter')}>
            {['all', 'gol', 'tarjeta', 'sustitucion'].map(kind => <button type="button" key={kind}
              aria-pressed={eventFilter === kind} onClick={() => setEventFilter(kind)}>
              {t(`record.eventKinds.${kind}`)} ({events.filter(event => kind === 'all' || event.kind === kind).length})</button>)}
          </div>
          <div className="mr-event-columns">{sides.map((side, sideIndex) => {
            const sideEvents = events.filter(event => event.home === (sideIndex === 0)
              && (eventFilter === 'all' || event.kind === eventFilter));
            return <section key={side.key} className="mr-event-team"
              aria-label={`${side.name} · ${t(sideIndex === 0 ? 'record.home' : 'record.away')}`}>
              <header className="mr-event-team-heading"><Avatar src={side.shield} alt="" />
                <div><h3>{side.name}</h3><small>{t(sideIndex === 0 ? 'record.home' : 'record.away')}</small></div>
                <span>{sideEvents.length}</span></header>
            </section>;
          })}</div>
          <ol className="mr-events mr-events-split" aria-label={t('tabs.events')}>
            {events.filter(event => eventFilter === 'all' || event.kind === eventFilter).map((event, index) => {
            const red = String(event.tipo ?? event.tipoTarjeta) === '101';
            const tone = event.kind === 'gol' ? 'success' : event.kind === 'sustitucion' ? 'primary' : red ? 'error' : 'warning';
            const colors = getEventRowColors(theme, tone);
            return <li key={index} className={event.home ? 'mr-event-home' : 'mr-event-away'}
              style={{ backgroundColor: colors.backgroundColor, borderColor: colors.borderColor, '--mr-event-row': index + 1 }}>
              <span className="mr-event-minute">{matchValue(event.minuto ?? event.minute)}′</span>
              <span className="mr-event-icon" aria-label={t(`record.eventKinds.${event.kind}`)}>
                {event.kind === 'gol' ? <SportsSoccer /> : event.kind === 'sustitucion' ? <SwapHoriz /> : <i className={`mr-card-icon${red || event.esSegundaAmarilla ? ' mr-red-card' : ''}`} />}</span>
              <div>{event.kind === 'sustitucion' ? <><strong className="mr-sub-in">{matchPlayerName(event.nombreJugadorEntra)}
                {event.numeroJugadorEntra != null && ` · ${event.numeroJugadorEntra}`}</strong>
                <span className="mr-sub-out">{matchPlayerName(event.nombreJugadorSale)}{event.numeroJugadorSale != null && ` · ${event.numeroJugadorSale}`}</span></>
                : <strong>{matchPlayerName(event.nombreJugador)}
                  {event.kind === 'tarjeta' && ` · ${t(event.esSegundaAmarilla ? 'record.secondYellow' : red ? 'record.redCard' : 'record.yellowCard')}`}
                  {event.kind === 'gol' && String(event.tipoGol) === '101' && ` · ${t('record.penalty')}`}
                  {event.kind === 'gol' && String(event.tipoGol) === '102' && ` · ${t('record.ownGoal')}`}</strong>}
                <small>{event.home ? partido.equipoLocal : partido.equipoVisitante}</small></div>
            </li>;
          })}</ol>
          {!events.filter(event => eventFilter === 'all' || event.kind === eventFilter).length
            && <p className="mr-empty">{t('record.noEvents')}</p>}
        </section>
      </>}
      {active === 'info' && <>
        <section className="mr-panel"><span className="mr-eyebrow">{t('record.staff')}</span>
          <div className="mr-coaches">{['Local', 'Visitante'].map(suffix => {
            const coaches = acta[`entrenadores${suffix === 'Local' ? 'Locales' : 'Visitantes'}`];
            const names = Array.isArray(coaches) && coaches.length ? coaches.map(coach => coach.nombre || coach.nombreEntrenador)
              : [...new Set([acta[`entrenador${suffix}`], acta[`entrenador2${suffix}`]].filter(Boolean))];
            return <div key={suffix}><h3>{t(`record.coach${suffix}`)}</h3>
              {(names.length ? names : [null]).map((name, index) => <p key={index}>{name ? matchPlayerName(name) : '—'}</p>)}</div>;
          })}</div>
        </section>
        {acta.arbitros?.length > 0 && <section className="mr-panel"><h2>{t('tabs.referees')}</h2>
          <ul className="mr-referees">{acta.arbitros.map((referee, index) => <li key={referee.id || index}>
            <strong>{matchPlayerName(referee.nombreArbitro)}</strong><span>{referee.tipoArbitro}</span></li>)}</ul>
        </section>}
        {localCards + awayCards > 0 && <section className="mr-panel"><h2>{t('record.discipline')}</h2>
          <dl className="mr-discipline">{[[t('record.totalCards'), localCards + awayCards], [partido.equipoLocal, localCards],
            [partido.equipoVisitante, awayCards]].map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{matchValue(value)}</dd></div>)}</dl>
        </section>}
      </>}
    </div>
    {!canUseScouting && <UpgradeBoundary permission="scouting.view" />}
  </article>;
}
